import React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AppLayout } from '../../components/layout/AppLayout'
import { Chip } from '../../components/ui/Chip'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { SlidePanel } from '../../components/ui/SlidePanel'
import { KommerSnart } from '../../components/ui/KommerSnart'
import { apiFetch } from '../../lib/api-client'
import { EnergyBadge } from '../../components/EnergyBadge'
import { KontakterTab, OpgaverTab, DokumenterTab, BillederTab } from '../../components/surfaces/SurfaceTabs'
import { KontokortTab } from './KontokortTab'
import { RentSharesSection } from './RentSharesSection'
import { CustomFieldsPanel } from '../../components/surfaces/CustomFieldsPanel'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Lease {
  id: string
  tenant_id: string
  property_id: string
  unit_no: string
  type: string
  area_m2: number | null
  room_count: number | null
  floor: string | null
  serial_number: number | null
  dawa_address_id: string | null
  status: string
  energy_label: string | null
  energy_label_valid_from: string | null
  energy_label_valid_to: string | null
  regulated_municipality: boolean
  beboerrepraesentation: boolean
  notes: string | null
}

interface Property {
  id: string
  address: string
  serial_label: string
}

interface Contract {
  id: string
  lease_id: string
  type: string
  monthly_rent_oere: number
  start_date: string
  end_date: string | null
  status: string
  serial_number: number | null
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function typeLabel(t: string): string {
  const map: Record<string, string> = {
    residential: 'Bolig', commercial: 'Erhverv',
    garage: 'Garage/carport', storage: 'Depotrum',
  }
  return map[t] ?? t
}

function statusLabel(s: string): string {
  const map: Record<string, string> = { ledig: 'Ledig', udlejet: 'Udlejet', renovering: 'Under renovering' }
  return map[s] ?? s
}

function statusVariant(s: string): 'green' | 'amber' | 'gray' {
  return s === 'udlejet' ? 'green' : s === 'ledig' ? 'amber' : 'gray'
}

function contractStatusVariant(s: string): 'green' | 'blue' | 'purple' | 'gray' | 'red' {
  switch (s) {
    case 'active': return 'green'
    case 'signed': return 'blue'
    case 'sent': return 'purple'
    case 'draft': return 'gray'
    case 'terminated': return 'red'
    default: return 'gray'
  }
}

function contractStatusLabel(s: string): string {
  const map: Record<string, string> = {
    draft: 'Kladde', sent: 'Sendt', signed: 'Underskrevet',
    active: 'Aktiv', terminated: 'Opsagt',
  }
  return map[s] ?? s
}

function fmtRent(oere: number): string {
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(oere / 100)
}

function fmtDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' })
}

// Formats a date-only string (YYYY-MM-DD) in local time, avoiding the UTC
// midnight shift that new Date('YYYY-MM-DD') introduces.
function fmtDateOnly(d: string): string {
  const [y, m, day] = d.split('-').map(Number)
  if (!y || !m || !day) return new Date(d).toLocaleDateString('da-DK')
  return new Date(y, m - 1, day).toLocaleDateString('da-DK')
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <td style={{ padding: '10px 24px', fontSize: 13, color: '#6b7280', width: 200, whiteSpace: 'nowrap' }}>
        {label}
      </td>
      <td style={{ padding: '10px 24px', fontSize: 13, color: '#111827', fontWeight: 500 }}>
        {value ?? <span style={{ color: '#d1d5db' }}>—</span>}
      </td>
    </tr>
  )
}

interface LeaseStats {
  has_active_contract: boolean
  maanedsleje_oere: number
  depositum_oere: number
  forudbetalt_oere: number
  start_date: string | null
  end_date: string | null
  opkraevet_til_dato_oere: number
}

// Nøgletal — current figures from existing data (active contract + total billed).
// Historical rent/index is a separate, later epic.
function NogletalTab({ leaseId }: { leaseId: string }) {
  const [stats, setStats] = React.useState<LeaseStats | null>(null)
  const [loading, setLoading] = React.useState(true)
  React.useEffect(() => {
    apiFetch<LeaseStats>(`/api/v1/leases/${leaseId}/stats`).then(setStats).catch(() => {}).finally(() => setLoading(false))
  }, [leaseId])

  const kr = (oere: number) => (oere / 100).toLocaleString('da-DK', { minimumFractionDigits: 0 }) + ' kr.'
  const dkDate = (s: string | null) => (s ? new Date(s).toLocaleDateString('da-DK') : null)

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10 }}>
        <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8eaed' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Nøgletal</span>
        </div>
        {stats && !stats.has_active_contract ? (
          <div style={{ padding: '20px 24px', fontSize: 13, color: '#9ca3af' }}>
            Ingen aktiv lejekontrakt på lejemålet endnu.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Månedsleje" value={stats ? kr(stats.maanedsleje_oere) : null} />
              <Row label="Depositum" value={stats ? kr(stats.depositum_oere) : null} />
              <Row label="Forudbetalt leje" value={stats ? kr(stats.forudbetalt_oere) : null} />
              <Row label="Indflytning" value={stats ? dkDate(stats.start_date) : null} />
              <Row label="Lejeperiode" value={stats ? (dkDate(stats.end_date) ? `Tidsbegrænset til ${dkDate(stats.end_date)}` : 'Tidsubegrænset') : null} />
              <Row label="Opkrævet til dato" value={stats ? kr(stats.opkraevet_til_dato_oere) : null} />
            </tbody>
          </table>
        )}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #f1f5f9', fontSize: 11.5, color: '#9ca3af' }}>
          Historiske lejedata og huslejeindeks følger senere.
        </div>
      </div>
    </div>
  )
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type Tab = 'overblik' | 'lejeforhold' | 'okonomi' | 'nogleetal' | 'dokumenter' | 'billeder' | 'kommunikation' | 'opgaver' | 'kontakter' | 'opsaetning'

const TABS: { key: Tab; label: string }[] = [
  { key: 'overblik', label: 'Overblik' },
  { key: 'lejeforhold', label: 'Lejeforhold' },
  { key: 'okonomi', label: 'Økonomi' },
  { key: 'nogleetal', label: 'Nøgletal' },
  { key: 'dokumenter', label: 'Dokumenter' },
  { key: 'billeder', label: 'Billeder' },
  { key: 'kommunikation', label: 'Kommunikation' },
  { key: 'opgaver', label: 'Opgaver' },
  { key: 'kontakter', label: 'Kontakter' },
  { key: 'opsaetning', label: 'Opsætning' },
]

// ─── Edit Stamdata Panel ──────────────────────────────────────────────────────

function EditStamdataPanel({ lease, onClose, onSaved }: {
  lease: Lease
  onClose: () => void
  onSaved: (l: Lease) => void
}) {
  const [form, setForm] = React.useState({
    unit_no: lease.unit_no,
    area_m2: lease.area_m2 != null ? String(lease.area_m2) : '',
    room_count: lease.room_count != null ? String(lease.room_count) : '',
    floor: lease.floor != null ? String(lease.floor) : '',
    energy_label: lease.energy_label ?? '',
    energy_label_valid_from: lease.energy_label_valid_from ?? '',
    energy_label_valid_to: lease.energy_label_valid_to ?? '',
  })
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const payload: Record<string, unknown> = {
        unit_no: form.unit_no.trim(),
        area_m2: form.area_m2 ? parseFloat(form.area_m2) : null,
        room_count: form.room_count ? parseInt(form.room_count) : null,
        floor: form.floor || null,
        energy_label: form.energy_label || null,
        energy_label_valid_from: form.energy_label_valid_from || null,
        energy_label_valid_to: form.energy_label_valid_to || null,
      }
      const updated = await apiFetch<Lease>(`/api/v1/leases/${lease.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })
      onSaved(updated)
      onClose()
    } catch {
      setError('Kunne ikke gemme ændringer. Prøv igen.')
    } finally {
      setSaving(false)
    }
  }

  const inputWrap: React.CSSProperties = { marginBottom: 14 }
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }

  return (
    <form onSubmit={handleSave}>
      <div style={inputWrap}>
        <Input label="Enhed / Dør" value={form.unit_no} onChange={(e) => setForm({ ...form, unit_no: e.target.value })} autoFocus />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <Input label="Areal (m²)" type="number" value={form.area_m2} onChange={(e) => setForm({ ...form, area_m2: e.target.value })} />
        <Input label="Antal rum" type="number" value={form.room_count} onChange={(e) => setForm({ ...form, room_count: e.target.value })} />
      </div>
      <div style={inputWrap}>
        <Input label="Etage" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} placeholder="st, kl, 1, 2 …" />
      </div>
      <div style={inputWrap}>
        <label style={labelStyle}>Energimærke</label>
        <select
          value={form.energy_label}
          onChange={(e) => setForm({ ...form, energy_label: e.target.value })}
          style={{ height: 40, padding: '0 12px', borderRadius: 7, border: '1px solid #e8eaed', fontSize: 14, width: '100%', fontFamily: 'inherit' }}
        >
          <option value="">—</option>
          {['A2020', 'A2015', 'A2010', 'A', 'B', 'C', 'D', 'E', 'F', 'G'].map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Energimærke gyldig fra</label>
          <input type="date" value={form.energy_label_valid_from} onChange={(e) => setForm({ ...form, energy_label_valid_from: e.target.value })}
            style={{ height: 40, padding: '0 12px', borderRadius: 7, border: '1px solid #e8eaed', fontSize: 14, width: '100%', fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={labelStyle}>Energimærke gyldig til</label>
          <input type="date" value={form.energy_label_valid_to} onChange={(e) => setForm({ ...form, energy_label_valid_to: e.target.value })}
            style={{ height: 40, padding: '0 12px', borderRadius: 7, border: '1px solid #e8eaed', fontSize: 14, width: '100%', fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
      </div>

      {error && <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{error}</p>}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" isLoading={saving}>Gem</Button>
        <Button type="button" variant="secondary" onClick={onClose}>Annuller</Button>
      </div>
    </form>
  )
}

// ─── Overblik Tab ─────────────────────────────────────────────────────────────

function OverblikTab({ lease, property, onUpdated }: {
  lease: Lease
  property: Property | null
  onUpdated: (l: Lease) => void
}) {
  const [editOpen, setEditOpen] = React.useState(false)
  const [notes, setNotes] = React.useState(lease.notes ?? '')
  const [savingNotes, setSavingNotes] = React.useState(false)

  const handleNotesBlur = async () => {
    if (notes === (lease.notes ?? '')) return
    setSavingNotes(true)
    try {
      const updated = await apiFetch<Lease>(`/api/v1/leases/${lease.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ notes: notes || null }),
      })
      onUpdated(updated)
    } catch { /* ignore */ } finally {
      setSavingNotes(false)
    }
  }

  const leaseSerial = lease.serial_number != null ? String(lease.serial_number).padStart(3, '0') : '—'
  const fullSerial = property ? `${property.serial_label}-${leaseSerial}` : leaseSerial

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>
      <div>
        <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10 }}>
          <div style={{
            padding: '14px 24px', borderBottom: '1px solid #e8eaed',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Stamdata
            </span>
            <Button size="sm" onClick={() => setEditOpen(true)}>Rediger</Button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Løbenummer" value={
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#4f46e5' }}>{fullSerial}</span>
              } />
              <Row label="Status" value={<Chip variant={statusVariant(lease.status)}>{statusLabel(lease.status)}</Chip>} />
              <Row label="Ejendom" value={
                property
                  ? <Link to={`/ejendomme/${lease.property_id}`} style={{ color: '#4f46e5', textDecoration: 'none' }}>{property.address}</Link>
                  : null
              } />
              <Row label="Enhed / Dør" value={lease.unit_no} />
              <Row label="Type" value={typeLabel(lease.type)} />
              <Row label="Areal" value={lease.area_m2 != null ? `${lease.area_m2} m²` : null} />
              <Row label="Antal rum" value={lease.room_count != null ? String(lease.room_count) : null} />
              <Row label="Etage" value={lease.floor && lease.floor.trim() !== '' ? lease.floor : null} />
              <Row label="Energimærke" value={
                lease.energy_label ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <EnergyBadge label={lease.energy_label} />
                    {lease.energy_label_valid_to && (() => {
                      const [y, m, d] = (lease.energy_label_valid_to as string).split('-').map(Number)
                      const displayDate = new Date(y, m - 1, d).toLocaleDateString('da-DK')
                      return (
                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                          Gyldigt til {displayDate}
                        </span>
                      )
                    })()}
                    <button
                      onClick={async () => {
                        try {
                          const r = await apiFetch<{ url: string }>(`/api/v1/leases/${lease.id}/energimaerke-url`)
                          window.open(r.url, '_blank')
                        } catch {
                          alert('Kunne ikke hente energimærke — prøv igen')
                        }
                      }}
                      style={{
                        padding: '3px 10px',
                        background: '#f3f4f6', border: '1px solid #d1d5db',
                        borderRadius: 6, fontSize: 12, cursor: 'pointer',
                      }}
                    >
                      Hent
                    </button>
                  </div>
                ) : null
              } />
              <Row label="Energimærke gyldig fra" value={
                lease.energy_label_valid_from ? fmtDateOnly(lease.energy_label_valid_from) : null
              } />
              <Row label="Reguleret kommune" value={lease.regulated_municipality ? 'Ja' : 'Nej'} />
              <Row label="Beboerrepræsentation" value={lease.beboerrepraesentation ? 'Ja' : 'Nej'} />
            </tbody>
          </table>
        </div>

        <CustomFieldsPanel basePath={`/api/v1/leases/${lease.id}`} />
      </div>

      <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10 }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #e8eaed',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Notat
          </span>
          {savingNotes && <span style={{ fontSize: 11, color: '#9ca3af' }}>Gemmer…</span>}
        </div>
        <div style={{ padding: 16 }}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={handleNotesBlur}
            placeholder="Notater kan anføres her..."
            style={{
              width: '100%', minHeight: 140, border: '1px solid #e8eaed',
              borderRadius: 6, padding: 10, fontSize: 13, color: '#111827',
              fontFamily: 'inherit', resize: 'vertical', outline: 'none',
              boxSizing: 'border-box', lineHeight: 1.5,
            }}
          />
        </div>
      </div>

      <SlidePanel isOpen={editOpen} onClose={() => setEditOpen(false)} title="Rediger lejemål">
        <EditStamdataPanel lease={lease} onClose={() => setEditOpen(false)} onSaved={onUpdated} />
      </SlidePanel>
    </div>
  )
}

// ─── Lejeforhold Tab ──────────────────────────────────────────────────────────

function LejeforholdTab({ lease }: { lease: Lease }) {
  const navigate = useNavigate()
  const [contracts, setContracts] = React.useState<Contract[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    apiFetch<Contract[]>(`/api/v1/contracts?lease_id=${lease.id}`)
      .then(setContracts)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [lease.id])

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{
        padding: '14px 24px', borderBottom: '1px solid #e8eaed',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Lejeforhold ({loading ? '…' : contracts.length})
        </span>
        <Button size="sm" onClick={() => navigate(`/kontrakter/opret?lease_id=${lease.id}`)}>
          + Opret lejeforhold
        </Button>
      </div>

      {loading && (
        <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>Henter…</div>
      )}

      {!loading && contracts.length === 0 && (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>Ingen lejeforhold under dette lejemål.</p>
          <Button size="sm" onClick={() => navigate(`/kontrakter/opret?lease_id=${lease.id}`)}>
            + Opret lejeforhold
          </Button>
        </div>
      )}

      {!loading && contracts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['Type', 'Indflytning', 'Ophør', 'Månedsleje', 'Status', ''].map((h) => (
                <th key={h} style={{
                  padding: '10px 24px', textAlign: 'left', fontSize: 11,
                  fontWeight: 600, color: '#6b7280', textTransform: 'uppercase',
                  letterSpacing: '0.05em', borderBottom: '1px solid #e8eaed',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contracts.map((c, i) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/lejeforhold/${c.id}`)}
                style={{ borderBottom: i < contracts.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer' }}
              >
                <td style={{ padding: '12px 24px', textTransform: 'uppercase', fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{c.type}</td>
                <td style={{ padding: '12px 24px', color: '#111827' }}>{fmtDate(c.start_date)}</td>
                <td style={{ padding: '12px 24px', color: '#6b7280' }}>{c.end_date ? fmtDate(c.end_date) : <span style={{ color: '#9ca3af' }}>Løbende</span>}</td>
                <td style={{ padding: '12px 24px', color: '#111827', fontWeight: 500 }}>{fmtRent(c.monthly_rent_oere)}</td>
                <td style={{ padding: '12px 24px' }}>
                  <Chip variant={contractStatusVariant(c.status)}>{contractStatusLabel(c.status)}</Chip>
                </td>
                <td style={{ padding: '12px 24px', textAlign: 'right' }}>
                  <span style={{ color: '#9ca3af', fontSize: 13 }}>→</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ─── Opsætning Tab ────────────────────────────────────────────────────────────

function OpsaetningTab({ lease, onUpdated }: { lease: Lease; onUpdated: (l: Lease) => void }) {
  const [form, setForm] = React.useState({
    status: lease.status,
    regulated_municipality: lease.regulated_municipality,
    beboerrepraesentation: lease.beboerrepraesentation,
  })
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      const updated = await apiFetch<Lease>(`/api/v1/leases/${lease.id}`, {
        method: 'PATCH',
        body: JSON.stringify(form),
      })
      onUpdated(updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch { /* ignore */ } finally {
      setSaving(false)
    }
  }

  const checkboxRow = (label: string, key: 'regulated_municipality' | 'beboerrepraesentation') => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 13, cursor: 'pointer' }}>
      <input
        type="checkbox"
        checked={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
        style={{ width: 16, height: 16 }}
      />
      {label}
    </label>
  )

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8eaed' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Lejemålets indstillinger
        </span>
      </div>
      <div style={{ padding: '20px 24px' }}>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: 6 }}>
            Status
          </label>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            style={{ height: 40, padding: '0 12px', borderRadius: 7, border: '1px solid #e8eaed', fontSize: 14, fontFamily: 'inherit' }}
          >
            <option value="ledig">Ledig</option>
            <option value="udlejet">Udlejet</option>
            <option value="renovering">Under renovering</option>
          </select>
        </div>
        {checkboxRow('Reguleret kommune', 'regulated_municipality')}
        {checkboxRow('Beboerrepræsentation', 'beboerrepraesentation')}
        <Button onClick={handleSave} isLoading={saving}>
          {saved ? 'Gemt ✓' : 'Gem ændringer'}
        </Button>
      </div>
    </div>
  )
}

// ─── Page Shell ───────────────────────────────────────────────────────────────

export function LeaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [lease, setLease] = React.useState<Lease | null>(null)
  const [property, setProperty] = React.useState<Property | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [notFound, setNotFound] = React.useState(false)
  const [activeTab, setActiveTab] = React.useState<Tab>('overblik')

  React.useEffect(() => {
    if (!id) return
    apiFetch<Lease>(`/api/v1/leases/${id}`)
      .then(async (l) => {
        setLease(l)
        const pData = await apiFetch<{ items: Property[] }>('/api/v1/properties')
        const p = pData.items.find((x) => x.id === l.property_id)
        if (p) setProperty(p)
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <AppLayout>
        <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Henter…</div>
      </AppLayout>
    )
  }

  if (notFound || !lease) {
    return (
      <AppLayout>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Lejemålet blev ikke fundet.</p>
          <Link to="/lejemaal"><Button variant="secondary">← Tilbage</Button></Link>
        </div>
      </AppLayout>
    )
  }

  const leaseSerial = lease.serial_number != null ? String(lease.serial_number).padStart(3, '0') : '—'
  const fullSerial = property ? `${property.serial_label}-${leaseSerial}` : leaseSerial

  return (
    <AppLayout>
      {/* Header */}
      <div style={{ padding: '0 0 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            fontFamily: 'monospace', fontSize: 13, fontWeight: 700,
            color: '#4f46e5', background: '#eff6ff', padding: '3px 8px', borderRadius: 5,
          }}>
            {fullSerial}
          </span>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
              {property?.address ?? '—'} · {lease.unit_no}
            </h1>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{typeLabel(lease.type)}</span>
          </div>
          <Chip variant={statusVariant(lease.status)}>{statusLabel(lease.status)}</Chip>
        </div>
        <Button size="sm" onClick={() => navigate(`/kontrakter/opret?lease_id=${lease.id}`)}>
          + Opret lejeforhold
        </Button>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e8eaed', marginBottom: 20, overflowX: 'auto' }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '11px 16px', fontSize: 13,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? '#111827' : '#6b7280',
              background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #4f46e5' : '2px solid transparent',
              marginBottom: -1, cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'color 0.12s, border-color 0.12s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overblik' && <OverblikTab lease={lease} property={property} onUpdated={setLease} />}
      {activeTab === 'lejeforhold' && <LejeforholdTab lease={lease} />}
      {activeTab === 'okonomi' && (
        <>
          <KontokortTab leaseId={lease.id} />
          <RentSharesSection leaseId={lease.id} />
        </>
      )}
      {activeTab === 'nogleetal' && <NogletalTab leaseId={lease.id} />}
      {activeTab === 'dokumenter' && <DokumenterTab basePath={`/api/v1/leases/${lease.id}`} />}
      {activeTab === 'billeder' && <BillederTab basePath={`/api/v1/leases/${lease.id}`} />}
      {activeTab === 'kommunikation' && <KommerSnart />}
      {activeTab === 'opgaver' && <OpgaverTab basePath={`/api/v1/leases/${lease.id}`} />}
      {activeTab === 'kontakter' && <KontakterTab basePath={`/api/v1/leases/${lease.id}`} />}
      {activeTab === 'opsaetning' && <OpsaetningTab lease={lease} onUpdated={setLease} />}
    </AppLayout>
  )
}
